import React from "react";

/**
 * Minimal Markdown renderer covering exactly the constructs used in /legal.
 *
 * Deliberately not `react-markdown` or `dangerouslySetInnerHTML`. These pages
 * carry contractual text, so the render path is the one place where an
 * injection or a silently-dropped clause would matter most. Producing React
 * nodes directly means there is no HTML string to sanitise and no third-party
 * parser deciding what a clause means.
 *
 * Supported: headings, paragraphs, bullet and numbered lists, pipe tables,
 * horizontal rules, bold, italic, inline code, and links. Anything else renders
 * as literal text rather than disappearing - a legal document that quietly
 * loses a line is worse than one that shows a stray asterisk.
 */

const INLINE = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;

function href(raw) {
  // Cross-references are written as ./file.md so the documents also read
  // correctly in the repo. In the app they resolve to /legal/<slug>.
  const m = raw.match(/^\.\/([a-z-]+)\.md$/);
  if (!m) return raw;
  const map = {
    "terms-of-service": "terms",
    "privacy-policy": "privacy",
    "data-methodology": "methodology",
    "advisory-disclaimer": "disclaimer",
    "services-agreement": "services-agreement",
    "sweep-consent": "sweep-consent",
  };
  return `/legal/${map[m[1]] || m[1]}`;
}

function inline(text, keyBase) {
  const parts = String(text).split(INLINE).filter((p) => p !== "" && p !== undefined);
  return parts.map((p, i) => {
    const k = `${keyBase}-${i}`;
    if (/^\*\*[^*]+\*\*$/.test(p)) return <strong key={k} className="text-[#171717] font-semibold">{p.slice(2, -2)}</strong>;
    if (/^\*[^*]+\*$/.test(p)) return <em key={k}>{p.slice(1, -1)}</em>;
    if (/^`[^`]+`$/.test(p)) return <code key={k} className="px-1 py-0.5 bg-[#e2ddd4] rounded text-[0.92em]">{p.slice(1, -1)}</code>;
    const link = p.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) {
      const to = href(link[2]);
      const external = /^https?:\/\//.test(to);
      return (
        <a
          key={k}
          href={to}
          {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          className="text-[#9a3412] underline underline-offset-2 hover:text-[#171717]"
        >
          {link[1]}
        </a>
      );
    }
    return <React.Fragment key={k}>{p}</React.Fragment>;
  });
}

function splitRow(line) {
  return line.replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
}

export default function Markdown({ source = "" }) {
  const lines = String(source).replace(/<!--[\s\S]*?-->/g, "").split("\n");
  const out = [];
  let i = 0;
  let key = 0;

  const flushParagraph = (buf) => {
    if (!buf.length) return;
    out.push(
      <p key={`p${key++}`} className="mb-4 leading-relaxed text-[#404040]">
        {inline(buf.join(" "), `p${key}`)}
      </p>
    );
  };

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) { i++; continue; }

    // Horizontal rule
    if (/^---+\s*$/.test(line)) {
      out.push(<hr key={`hr${key++}`} className="my-8 border-0 border-t border-[#c9c3b8]" />);
      i++; continue;
    }

    // Headings
    const h = line.match(/^(#{1,4})\s+(.*)$/);
    if (h) {
      const level = h[1].length;
      const text = inline(h[2], `h${key}`);
      const cls = {
        1: "font-serif-display text-4xl md:text-5xl text-[#171717] leading-tight mt-2 mb-6",
        2: "font-serif-display text-2xl md:text-3xl text-[#171717] leading-snug mt-10 mb-4",
        3: "font-mono-tech text-[13px] uppercase tracking-widest text-[#9a3412] mt-8 mb-3",
        4: "font-mono-tech text-[12px] uppercase tracking-widest text-[#525252] mt-6 mb-2",
      }[level];
      const Tag = `h${Math.min(level, 6)}`;
      out.push(<Tag key={`h${key++}`} className={cls}>{text}</Tag>);
      i++; continue;
    }

    // Pipe table
    if (line.trim().startsWith("|") && (lines[i + 1] || "").includes("---")) {
      const head = splitRow(line);
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        rows.push(splitRow(lines[i])); i++;
      }
      out.push(
        <div key={`t${key++}`} className="my-6 overflow-x-auto">
          <table className="w-full text-[13px] font-mono-tech border-collapse">
            <thead>
              <tr className="border-b border-[#a8a196]">
                {head.map((c, n) => (
                  <th key={n} className="text-left py-2 pr-4 font-semibold text-[#171717]">{inline(c, `th${n}`)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, n) => (
                <tr key={n} className="border-b border-[#ddd8ce]">
                  {r.map((c, m) => (
                    <td key={m} className="py-2 pr-4 align-top text-[#404040]">{inline(c, `td${n}-${m}`)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // Bullet list
    if (/^[-*]\s+/.test(line.trim())) {
      const items = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^[-*]\s+/, "")); i++;
      }
      out.push(
        <ul key={`ul${key++}`} className="mb-5 pl-5 space-y-2 list-disc marker:text-[#e88a51]">
          {items.map((t, n) => (
            <li key={n} className="leading-relaxed text-[#404040]">{inline(t, `li${n}`)}</li>
          ))}
        </ul>
      );
      continue;
    }

    // Numbered list
    if (/^\d+\.\s+/.test(line.trim())) {
      const items = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+\.\s+/, "")); i++;
      }
      out.push(
        <ol key={`ol${key++}`} className="mb-5 pl-5 space-y-2 list-decimal marker:text-[#9a3412]">
          {items.map((t, n) => (
            <li key={n} className="leading-relaxed text-[#404040]">{inline(t, `oli${n}`)}</li>
          ))}
        </ol>
      );
      continue;
    }

    // Paragraph - collect until a blank line or a construct starts
    const buf = [];
    while (
      i < lines.length && lines[i].trim() &&
      !/^(#{1,4}\s|[-*]\s|\d+\.\s|\|)/.test(lines[i].trim()) &&
      !/^---+\s*$/.test(lines[i])
    ) {
      buf.push(lines[i].trim()); i++;
    }
    flushParagraph(buf);
  }

  return <div className="max-w-none">{out}</div>;
}
